import { useState } from "react";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Spinner } from "@/components/States";
import { TYPES } from "@/components/project/prestart/meta";

const blankRow = () => ({ type: "question", title: "", detail: "", shared: null });

function TemplateEditor({ tpl, onClose, onSaved }) {
  const copy = tpl.builtin;
  const [name, setName] = useState(copy ? "My starter questions" : tpl.name);
  const [auto, setAuto] = useState(copy ? false : !!tpl.auto_apply);
  const [rows, setRows] = useState(tpl.items.length ? tpl.items.map((r) => ({ ...r })) : [blankRow()]);
  const [busy, setBusy] = useState(false);
  const setRow = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    setBusy(true);
    const body = { name, auto_apply: auto, items: rows.filter((r) => r.title.trim()).map((r) => ({ type: r.type, title: r.title.trim(), detail: r.detail || "", shared: r.shared })) };
    try {
      if (tpl.id && !copy) await api.put(`/workspace/prestart-templates/${tpl.id}`, body); else await api.post("/workspace/prestart-templates", body);
      toast.success("Template saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="template-editor">
        <SheetHeader><SheetTitle className="section-title">{copy ? "Save as your own template" : tpl.id ? "Edit template" : "New template"}</SheetTitle><SheetDescription>Each row becomes a planning item when the template is applied. Items already on the project (same title) are skipped.</SheetDescription></SheetHeader>
        <div className="mt-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="space-y-1.5"><Label htmlFor="tpl-name">Template name</Label><Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="template-name-input" /></div>
            <label className="flex items-center gap-2 pb-2 text-sm"><Switch checked={auto} onCheckedChange={setAuto} data-testid="template-auto-switch" />Add to every new project</label>
          </div>
          <ul className="space-y-2" data-testid="template-rows">
            {rows.map((r, i) => (
              <li key={i} className="grid gap-2 rounded-lg border p-2 sm:grid-cols-[140px_1fr_auto]" data-testid={`template-row-${i}`}>
                <select value={r.type} onChange={(e) => setRow(i, "type", e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" aria-label="Type" data-testid={`template-row-type-${i}`}>{Object.entries(TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}</select>
                <Input value={r.title} onChange={(e) => setRow(i, "title", e.target.value)} maxLength={160} placeholder="Question or task" data-testid={`template-row-title-${i}`} />
                <Button type="button" variant="ghost" size="icon" aria-label="Remove row" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`template-row-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setRows([...rows, blankRow()])} data-testid="template-add-row"><Plus className="mr-1.5 h-3.5 w-3.5" />Add row</Button>
          <p className="text-xs text-muted-foreground">Sharing follows the type's default (questions, client info, contacts, documents, products and suggestions are shared with the client; tasks, investigations and savings stay internal).</p>
          <Button className="w-full rounded-full" onClick={save} disabled={busy || !name.trim() || !rows.some((r) => r.title.trim())} data-testid="template-save">{busy ? <Spinner /> : "Save template"}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function PlanningTemplates() {
  const { data, reload } = useFetch("/workspace/prestart-templates");
  const [editing, setEditing] = useState(null);
  const del = async (t) => { try { await api.delete(`/workspace/prestart-templates/${t.id}`); toast.message("Template removed"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="planning-templates-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold"><ClipboardList className="h-4 w-4 text-muted-foreground" />Pre-start planning templates</h3>
          <p className="mt-1 text-sm text-muted-foreground">Reusable starter lists of questions and tasks. Drop one onto a project from its Pre-Start Planning tab, or have it added to every new project automatically.</p>
        </div>
        <Button size="sm" className="rounded-full" onClick={() => setEditing({ name: "", items: [], auto_apply: false })} data-testid="template-new"><Plus className="mr-1.5 h-4 w-4" />New template</Button>
      </div>
      {!data ? <Spinner /> : (
        <ul className="mt-4 divide-y" data-testid="template-list">
          {data.templates.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`template-${t.id}`}>
              <div className="min-w-0"><p className="text-sm font-medium">{t.name}{t.builtin && <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Built in</span>}{t.auto_apply && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">Every new project</span>}</p><p className="text-xs text-muted-foreground">{t.items.length} item{t.items.length === 1 ? "" : "s"}</p></div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}>{t.builtin ? "Save as my own" : "Edit"}</Button>
                {!t.builtin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(t)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing && <TemplateEditor tpl={editing} onClose={() => setEditing(null)} onSaved={reload} />}
    </section>
  );
}
